import { Phone, ArrowRight, CheckCircle2, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { companyInfo } from '../data/pricing';
import { useState } from 'react';
import { PlanUploadModal } from '../components/PlanUploadModal';

export const NewBuildElectrician = () => {
  const [showPlanUpload, setShowPlanUpload] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <SEO 
        title="New Build Electrician London | Complete Electrical Installation | Crescent Electrical"
        description="Professional electrical contractor for new build residential projects. Full electrical package from design to certification. Work with developers and main contractors across London."
        canonical="https://crescentelectrical.co.uk/new-build-electrician"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0B132B] to-[#1C2541] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
            NEW BUILD
            <span className="block text-[#0066FF] mt-2">ELECTRICAL CONTRACTOR</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl">
            Complete electrical installation packages for new residential construction. 
            From design and first fix through to testing, certification and handover.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              size="lg"
              onClick={() => window.location.href = '/rewire-quote'}
              className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
            >
              Request Project Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowPlanUpload(true)}
              className="border-2 border-white text-white hover:bg-white hover:text-[#0B132B]"
            >
              <Upload className="mr-2 h-5 w-5" />
              Send Your Plans
            </Button>
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-4">
              COMPLETE NEW BUILD ELECTRICAL PACKAGES
            </h2>
            <p className="text-lg text-[#475569] max-w-3xl mx-auto">
              We provide the full electrical installation for residential new build projects, 
              working alongside main contractors, developers and architects from foundation stage through to final handover.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#F8F9FA] rounded-xl p-8">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6">Design & Planning</h3>
              <ul className="space-y-3">
                {[
                  'Electrical installation design',
                  'Lighting layout plans',
                  'Power distribution design',
                  'Load calculations',
                  'Building Regulations compliance',
                  'Part P notification'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span className="text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#F8F9FA] rounded-xl p-8">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6">Installation</h3>
              <ul className="space-y-3">
                {[
                  'Consumer unit and distribution boards',
                  'First fix cable installation',
                  'Circuit routing and segregation',
                  'Second fix accessories',
                  'Light fittings and switches',
                  'All sockets and power points'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span className="text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#F8F9FA] rounded-xl p-8">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6">Systems & Features</h3>
              <ul className="space-y-3">
                {[
                  'Smoke and heat detection',
                  'External lighting and power',
                  'Garden electrical supplies',
                  'EV charger installation ready',
                  'Data and CAT6 networking',
                  'Smart home preparation'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span className="text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#F8F9FA] rounded-xl p-8">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6">Testing & Certification</h3>
              <ul className="space-y-3">
                {[
                  'Full circuit testing',
                  'Electrical Installation Certificate',
                  'Building Control notification',
                  'NICEIC certification',
                  'Test result documentation',
                  'Handover pack provided'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span className="text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-[#0F172A] text-center mb-12">
            New Build Installation Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: '01', title: 'DESIGN', desc: 'Electrical layouts and plans' },
              { step: '02', title: 'FIRST FIX', desc: 'Cable installation pre-plaster' },
              { step: '03', title: 'SECOND FIX', desc: 'Accessories and fittings' },
              { step: '04', title: 'TESTING', desc: 'Full electrical testing' },
              { step: '05', title: 'HANDOVER', desc: 'Certification and documentation' }
            ].map((phase) => (
              <div key={phase.step} className="bg-white rounded-xl p-6 border-2 border-gray-200 text-center">
                <div className="w-12 h-12 bg-[#0066FF] text-white rounded-xl flex items-center justify-center font-mono font-bold mx-auto mb-4">
                  {phase.step}
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{phase.title}</h3>
                <p className="text-sm text-[#475569]">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0B132B] to-[#1C2541] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold mb-6">Planning a New Build Project?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Send us your plans for a detailed electrical installation quotation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button 
              size="lg"
              onClick={() => window.location.href = '/rewire-quote'}
              className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
            >
              Get A Quote
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowPlanUpload(true)}
              className="border-2 border-white text-white hover:bg-white hover:text-[#0B132B]"
            >
              Upload Plans
            </Button>
          </div>
          <a 
            href={`tel:${companyInfo.phone}`}
            className="inline-flex items-center gap-2 text-[#0066FF] hover:text-[#F59E0B] font-semibold text-lg"
          >
            <Phone className="h-5 w-5" />
            {companyInfo.phone}
          </a>
        </div>
      </section>

      <PlanUploadModal isOpen={showPlanUpload} onClose={() => setShowPlanUpload(false)} />
    </div>
  );
};